import * as React from 'react';
import { useState, useEffect } from 'react';
import { Box, Typography, Tab, Tabs, SelectChangeEvent } from '@mui/material';
import axios from 'axios';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import TableLoader from '../GlobalComponents/TableLoader';
import PaymentTable from './PaymentTable';
import { CustomerGroups, processTransactionList, TransactionData } from './PaymentTypes';
import PaymentHistoryTable from './PaymentHistoryTable';
import { CustomerGroup, SelectedDataItem } from '../Machines/types';
import { API_PATH, appContext } from '../../../App';
import SearchFilter from './FilterBlock';
import BalanceDisplay from '../Wallets/BalanceDisplay';
import PoolPayments from './PoolPayments';

export default function PaymentsList() {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<string>("all");
    const [rows, setRows] = useState<TransactionData[]>([]);
    const [pendingRows, setPendingRows] = useState<TransactionData[]>([]);
    const [groupedTransactions, setGroupedTransactions] = useState<Record<string, TransactionData[]> | null>(null);
    const [loadingGroupedTransactions, setLoadingGroupedTransactions] = useState<boolean>(false);

    const [customerDataList, setCustomerDataList] = useState<SelectedDataItem[]>([]);
    const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
    const [fromDate, setFromDate] = useState<Date | null>(new Date());
    const [toDate, setToDate] = useState<Date | null>(new Date());
    const [selectOpen, setSelectOpen] = useState(false);
    const [customerGroupNames, setCustomerGroupNames] = useState<CustomerGroups[]>([]);

    const [historyTableData, setHistoryTableData] = useState<TransactionData[]>([]);
    const { authRole } = React.useContext(appContext);

    useEffect(() => {
        fetchCustomerGroupNames();
        getPreviousTransactions();
    }, []);


    useEffect(() => {
        fetchCustomerList();
        //fetchPendingList();
        // searchData('');
        fetchData()
    }, [customerGroupNames, activeTab]);



    const fetchCustomerList = () => {
        axios.get(API_PATH + '/segPoolRewardDistribution/poolusers', getAxiosConfig())
            .then(response => {
                const DataList: SelectedDataItem[] = response.data.map((item: CustomerGroup) => ({
                    id: item,
                    name: item
                }));
                setCustomerDataList(DataList);
            })
            .catch(error => {
                console.error("Error fetching customer list:", error);
            });
    };

    const fetchCustomerGroupNames = (): Promise<void> => {
        return axios
            .get(API_PATH + '/finance/report/customerlist', getAxiosConfig())
            .then((response) => {
                setCustomerGroupNames(response.data);
            })
            .catch((error) => {
                console.error("Error fetching customer list:", error);
            });
    };

    // customerGroupNames is scanned once per response instead of once per row
    const withCustomerGroup = (transactions: TransactionData[]): TransactionData[] => {
        const byBaseWorker = new Map(customerGroupNames.map((cg) => [cg.baseWorker, cg.customerGroupName]));
        return transactions.map((tx) => ({
            ...tx,
            customerGroupName: byBaseWorker.get(tx.username) ?? null,
        }));
    };

    const fetchPendingList = () => {
        if (!customerGroupNames || customerGroupNames.length === 0) {
            return;
        }

        setLoading(true);
        axios.get(`/api/segPoolRewardDistribution/pending`, getAxiosConfig())
            .then(response => {
                setPendingRows(processTransactionList(withCustomerGroup(response.data)));
                setLoading(loadingGroupedTransactions);
            })
            .catch(error => {
                console.error("Error fetching pending data:", error);
                setError("Failed to fetch data.");
                setLoading(false);
            });

    };


    const searchData = (status: string) => {
        setLoading(true);

        const formattedFromDate = fromDate
            ? `${fromDate.getUTCFullYear()}-${String(fromDate.getUTCMonth() + 1).padStart(2, "0")}-${String(fromDate.getUTCDate()).padStart(2, "0")}`
            : null;

        const formattedToDate = toDate
            ? `${toDate.getUTCFullYear()}-${String(toDate.getUTCMonth() + 1).padStart(2, "0")}-${String(toDate.getUTCDate()).padStart(2, "0")}`
            : null;

        axios.get(`/api/segPoolRewardDistribution/transactions?statuses=${status}&from=${formattedFromDate}&to=${formattedToDate}&users=${selectedCustomers.join(',')}`, getAxiosConfig())
            .then(response => {
                const mergedData = withCustomerGroup(response.data);
                switch (activeTab) {
                    case "all":
                        setHistoryTableData(mergedData);
                        break;
                    case "pending":
                        setPendingRows(processTransactionList(mergedData))
                        break;
                    case "failed":
                        setRows(processTransactionList(mergedData))

                        break;
                    default:
                        setHistoryTableData(mergedData);
                }
                setLoading(false);

            })
            .catch(error => {
                console.error("Error fetching transaction data:", error);
                setError("Failed to fetch data.");
                setLoading(false);
            });
    };

    const handleChange = (event: React.SyntheticEvent, newValue: string) => {
        setActiveTab(newValue);
    };

    const handleMultiChange = (event: SelectChangeEvent<string[]>) => {
        setSelectedCustomers(event.target.value as string[]);
        setSelectOpen(false);
    };

    const handleDelete = (nameToDelete: string) => {
        setSelectedCustomers((prev) => prev.filter((name) => name !== nameToDelete));
    };

    const fetchData = () => {
        switch (activeTab) {
            case "all":
                searchData('');
                break;
            case "pending":
                fetchPendingList();
                break;
            case "failed":
                searchData("TRANSFER_FAILED");
                break;
            case "pool":
                // PoolPayments loads its own data
                break;
            default:
                searchData('');
        }
    };

    const handleDownloadCSV = () => {
        const selectedData: TransactionData[] = activeTab === 'all' ? historyTableData : rows;

        if (!selectedData.length) {
            setError("No data to download.");
            return;
        }

        const csvRows = [
            [
                "ID",
                "User Name",
                "Customer",
                "Payment Mode",
                "Amount",
                "Transaction Status",
                "Created Time",
                "Transaction Time",
                "Network Txn ID",
                "Target Wallet",
                "Failed",
                "Hash Rate (TH)",
                "Raw Error",
            ],
            ...selectedData.map((tx) => [
                tx.id,
                tx.username,
                tx.customerGroupName,
                tx.paymentMode,
                tx.amount,
                tx.transactionStatus,
                tx.createdTime,
                tx.transactionTime,
                tx.networkTransactionId ?? "",
                tx.targetWallet ?? "",
                tx.failed,
                tx.hashRateInTh,
                tx.rawError ?? "",
            ]),
        ];

        const csvContent =
            "data:text/csv;charset=utf-8," +
            csvRows.map((e) => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Transaction_Report_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };


    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <BalanceDisplay />
            <Box>
                <Box sx={{ width: '100%', bgcolor: 'background.paper', marginBottom: "15px" }}>
                    <Tabs value={activeTab} onChange={handleChange} variant="scrollable" allowScrollButtonsMobile>
                        <Tab label="All Payments" value={"all"} />
                        {authRole === 'ADMIN' && < Tab label="Pending Payments" value={"pending"} />}
                        {authRole === 'ADMIN' && <Tab label="Failed Payments" value={"failed"} />}
                        {authRole === 'ADMIN' && <Tab label="Pool Payments" value={"pool"} />}

                    </Tabs>
                </Box>
                {activeTab != "pending" && activeTab !== "pool" &&
                    <SearchFilter
                        selectedCustomers={selectedCustomers}
                        setSelectedCustomers={setSelectedCustomers}
                        customerDataList={customerDataList}
                        fromDate={fromDate}
                        setFromDate={setFromDate}
                        toDate={toDate}
                        setToDate={setToDate}
                        handleMultiChange={handleMultiChange}
                        handleDelete={handleDelete}
                        fetchData={fetchData}
                        selectOpen={selectOpen}
                        setSelectOpen={setSelectOpen}
                        handleDownload={handleDownloadCSV}
                    />
                }
                {activeTab === "pool" ? (
                    <PoolPayments />
                ) : loading ? (
                    <TableLoader />
                ) : error ? (
                    <Typography color="error">{error}</Typography>
                ) : activeTab === "pending" ? (
                    rows && <PaymentTable rows={pendingRows} refresh={fetchPendingList} status={'pending'} recentTransactions={groupedTransactions} />
                ) : activeTab === "failed" ? (
                    rows && <PaymentTable rows={rows} refresh={fetchData} status={'failed'} recentTransactions={null} />
                ) : (
                    <PaymentHistoryTable data={historyTableData} />
                )}
            </Box>
        </Box>
    );

    function getPreviousTransactions() {
        if (loadingGroupedTransactions) return;
        setLoadingGroupedTransactions(true);
        if (groupedTransactions === null) {
            axios.get(`/api/segPoolRewardDistribution/recentTransactions`, getAxiosConfig())
                .then(response => {

                    const mergedData = processTransactionList(withCustomerGroup(response.data));

                    const groupedData: Record<string, any[]> = mergedData.reduce((acc: Record<string, any[]>, curr: any) => {
                        const { username, paymentMode } = curr;
                        if (!acc[username + " " + paymentMode]) {
                            acc[username + " " + paymentMode] = [];
                        }
                        curr.hashRateInTh = (curr.hashRateInTh ?? 0).toFixed(2);
                        acc[username + " " + paymentMode].push(curr);
                        return acc;
                    }, {});

                    Object.keys(groupedData).forEach(userPaymentMode => {
                        groupedData[userPaymentMode].sort((a: any, b: any) => {
                            return new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime();
                        });
                    });

                    console.log("Grouped and Sorted Data:", groupedData);
                    setGroupedTransactions(groupedData);
                    setLoadingGroupedTransactions(false);
                })
                .catch(error => {
                    console.error("Error fetching pending data:", error);
                    setError("Failed to fetch data.");
                    setLoadingGroupedTransactions(false);
                });
        }
    }
}
